import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import HeroSection from "@/components/blocks/HeroSection";
import CTAStrip from "@/components/blocks/CTAStrip";
import ExperienceCard from "@/components/blocks/ExperienceCard";
import ScrollReveal from "@/components/ScrollReveal";
import BrassRule from "@/components/BrassRule";
import { fetchExperiences } from "@/lib/api";
import { useLang, COUNTRY_LABELS, CITY_LABELS } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";
import { ChevronDown, X } from "lucide-react";

const COUNTRY_CODES = ["spain", "italy", "portugal"];

function Chip({ active, disabled, onClick, children, dataTestid }) {
  // Three explicit visual states only:
  //   • active   → solid deep-blue plaque (selected)
  //   • disabled → ghosted (no matches for current filter combination)
  //   • default  → light outline; on hover a subtle deep-blue stroke, no green/blue residue
  // Keyboard focus uses focus-visible (no browser outline on mouse-click).
  const base =
    "shrink-0 px-4 py-1.5 border eyebrow transition-all duration-300 " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-2 focus-visible:ring-offset-ivory";
  let look;
  if (disabled) {
    look = "bg-transparent text-charcoal/25 border-mist/40 cursor-not-allowed";
  } else if (active) {
    look = "bg-emerald text-ivory border-emerald";
  } else {
    look = "bg-transparent text-charcoal/70 border-mist hover:border-emerald hover:text-emerald";
  }
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      data-testid={dataTestid}
      data-active={active ? "true" : "false"}
      data-disabled={disabled ? "true" : undefined}
      className={`${base} ${look}`}
      style={{ borderRadius: 0, fontSize: 11, letterSpacing: "0.18em" }}
    >
      {children}
    </button>
  );
}

function ChipGroup({ label, children, dataTestid }) {
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid={dataTestid}>
      <span className="eyebrow text-brass" style={{ fontSize: 10 }}>{label}</span>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

export default function ExperiencesIndex() {
  const { lang, t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useSeo({
    title:
      lang === "es"
        ? "Experiencias — Viaja de Verdad"
        : "Experiences — Viaja de Verdad",
    description:
      lang === "es"
        ? "Veinte experiencias singulares en España, Italia y Portugal. Filtra por país o ciudad."
        : "Twenty singular experiences across Spain, Italy and Portugal. Filter by country or city.",
    image: "/static/experiences/agrippa-baths-candlelight/hero.png",
    locale: lang === "es" ? "es_ES" : "en_US",
    type: "website",
  });

  // Active filters drawn from the URL
  const country = searchParams.get("country") || "";
  const city = searchParams.get("city") || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchExperiences({ lang })
      .then((data) => { if (!cancelled) { setAll(data); setLoading(false); } })
      .catch((err) => {
        if (process.env.NODE_ENV === "development") {
          console.error("[VdV][ExperiencesIndex] fetch failed:", err);
        }
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [lang]);

  // Filter in memory (cheap, 20 items)
  const filtered = useMemo(() => {
    return all.filter((e) => {
      if (country && e.country !== country) return false;
      if (city && e.city !== city) return false;
      return true;
    });
  }, [all, country, city]);

  // Available cities, narrowed by country if one is selected
  const cityCodes = useMemo(() => {
    const codes = Object.keys(CITY_LABELS).filter(
      (c) => !country || CITY_LABELS[c].country === country,
    );
    return codes;
  }, [country]);

  // Compute the set of country / city codes that still have at least one match
  // given the *other* filter. Each chip is enabled iff selecting it would not
  // produce an empty grid. We always allow the chip that is currently active
  // (so the user can deselect it). "all" is always enabled.
  const { enabledCountries, enabledCities } = useMemo(() => {
    const matchesCountry = (exp, code) => !code || exp.country === code;
    const matchesCity = (exp, code) => !code || exp.city === code;

    const countries = new Set();
    const cities = new Set();

    for (const exp of all) {
      // Country chip enabled if there's any experience with that country
      // that also matches the current city filter.
      if (matchesCity(exp, city)) {
        countries.add(exp.country);
      }
      // City chip enabled if it matches the current country.
      if (matchesCountry(exp, country)) {
        cities.add(exp.city);
      }
    }

    // Always allow the currently selected one so the user can deselect.
    if (country) countries.add(country);
    if (city) cities.add(city);

    return { enabledCountries: countries, enabledCities: cities };
  }, [all, country, city]);

  // If selected city no longer in the available set after country change, clear it
  useEffect(() => {
    if (city && !cityCodes.includes(city)) {
      const next = new URLSearchParams(searchParams);
      next.delete("city");
      setSearchParams(next, { replace: true });
    }
  }, [cityCodes, city, searchParams, setSearchParams]);

  function setParam(key, value) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: false });
  }
  function clearAll() {
    setSearchParams({}, { replace: false });
  }

  const hasAnyFilter = !!(country || city);

  return (
    <div data-testid="experiences-index-page" className="bg-ivory">
      <HeroSection
        image="/static/experiences/agrippa-baths-candlelight/hero.png"
        imageAlt="Candlelight dinner inside the ancient Baths of Agrippa"
        eyebrow={t.collection.eyebrow}
        title={t.collection.title}
        subtitle={t.collection.subtitle}
        height="h-[68vh]"
      />

      <section className="bg-ivory">
        <div className="mx-auto max-w-3xl px-6 md:px-10 py-24 md:py-32 text-center">
          <ScrollReveal><p className="eyebrow text-charcoal/80">{t.collection.intro_eyebrow}</p></ScrollReveal>
          <ScrollReveal delay={120}>
            <h2 className="font-display text-charcoal mt-8 leading-[1.08] text-balance" style={{ fontSize: "clamp(1.85rem, 3.5vw, 2.75rem)" }}>
              {t.collection.intro_title}
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={220}>
            <div className="mt-10 space-y-6 text-charcoal/85 text-[17px] md:text-[18px] leading-[1.85] text-pretty">
              <p>{t.collection.intro_p1}</p>
              <p>{t.collection.intro_p2}</p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={320}><BrassRule className="mt-12" width={60} /></ScrollReveal>
        </div>
      </section>

      {/* ===== Sticky filter bar ===== */}
      <div className="sticky top-16 md:top-20 z-30 bg-ivory/95 backdrop-blur-md border-b border-mist" data-testid="filter-bar">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-4 md:py-5">
          {/* Desktop: chips inline */}
          <div className="hidden md:flex md:items-center md:gap-6 md:flex-wrap">
            <ChipGroup label={t.filters.country} dataTestid="chip-group-country">
              <Chip active={!country} onClick={() => setParam("country", "")} dataTestid="chip-country-all">{t.filters.all}</Chip>
              {COUNTRY_CODES.map((c) => (
                <Chip key={c} active={country === c} disabled={!enabledCountries.has(c)} onClick={() => setParam("country", country === c ? "" : c)} dataTestid={`chip-country-${c}`}>
                  {COUNTRY_LABELS[c][lang]}
                </Chip>
              ))}
            </ChipGroup>
            <span aria-hidden className="h-5 w-px bg-mist" />
            <ChipGroup label={t.filters.city} dataTestid="chip-group-city">
              <Chip active={!city} onClick={() => setParam("city", "")} dataTestid="chip-city-all">{t.filters.all}</Chip>
              {cityCodes.map((c) => (
                <Chip key={c} active={city === c} disabled={!enabledCities.has(c)} onClick={() => setParam("city", city === c ? "" : c)} dataTestid={`chip-city-${c}`}>
                  {CITY_LABELS[c][lang]}
                </Chip>
              ))}
            </ChipGroup>
            <span className="ml-auto eyebrow text-brass" data-testid="results-count" style={{ fontSize: 11 }}>
              {t.filters.showing} {filtered.length} {t.filters.of} {all.length}
            </span>
          </div>

          {/* Mobile: collapsible details */}
          <details className="md:hidden" open={mobileOpen} onToggle={(e) => setMobileOpen(e.currentTarget.open)}>
            <summary className="flex items-center justify-between cursor-pointer list-none" data-testid="filter-mobile-summary">
              <span className="eyebrow text-charcoal" style={{ fontSize: 12 }}>{t.filters.title}{hasAnyFilter ? " •" : ""}</span>
              <span className="eyebrow text-brass flex items-center gap-2" style={{ fontSize: 11 }}>
                {t.filters.showing} {filtered.length}/{all.length}
                <ChevronDown size={14} className={`transition-transform duration-300 ${mobileOpen ? "rotate-180" : ""}`} />
              </span>
            </summary>
            <div className="mt-5 space-y-4">
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-6 px-6">
                <span className="eyebrow text-brass self-center shrink-0" style={{ fontSize: 10 }}>{t.filters.country}</span>
                <Chip active={!country} onClick={() => setParam("country", "")}>{t.filters.all}</Chip>
                {COUNTRY_CODES.map((c) => (
                  <Chip key={c} active={country === c} disabled={!enabledCountries.has(c)} onClick={() => setParam("country", country === c ? "" : c)}>
                    {COUNTRY_LABELS[c][lang]}
                  </Chip>
                ))}
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-6 px-6">
                <span className="eyebrow text-brass self-center shrink-0" style={{ fontSize: 10 }}>{t.filters.city}</span>
                <Chip active={!city} onClick={() => setParam("city", "")}>{t.filters.all}</Chip>
                {cityCodes.map((c) => (
                  <Chip key={c} active={city === c} disabled={!enabledCities.has(c)} onClick={() => setParam("city", city === c ? "" : c)}>
                    {CITY_LABELS[c][lang]}
                  </Chip>
                ))}
              </div>
              {hasAnyFilter && (
                <button type="button" onClick={clearAll} className="eyebrow text-charcoal inline-flex items-center gap-2 link-underline" data-testid="clear-filters-mobile" style={{ fontSize: 11 }}>
                  <X size={12} /> {t.cta.clear}
                </button>
              )}
            </div>
          </details>
        </div>
      </div>

      {/* ===== Grid ===== */}
      <section className="bg-pearl py-20 md:py-24" style={{ backgroundColor: "var(--color-pearl)" }}>
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          {loading && (
            <p className="text-center italic-quote text-charcoal/60 py-16">…</p>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-24 text-center" data-testid="filter-empty-state">
              <BrassRule width={60} className="mx-auto" />
              <p className="italic-quote text-charcoal/80 mt-8 text-balance" style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.8rem)" }}>
                {t.filters.empty_title}
              </p>
              <p className="text-charcoal/60 mt-3">{t.filters.empty_sub}</p>
              <button type="button" onClick={clearAll} className="btn-brass mt-9 text-charcoal" data-testid="clear-filters-empty">{t.cta.clear}</button>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <FilteredGrid items={filtered} />
          )}
          {hasAnyFilter && filtered.length > 0 && (
            <div className="mt-14 text-center">
              <button type="button" onClick={clearAll} className="eyebrow text-charcoal/70 link-underline inline-flex items-center gap-2" data-testid="clear-filters" style={{ fontSize: 11 }}>
                <X size={12} /> {t.cta.clear}
              </button>
            </div>
          )}
        </div>
      </section>

      <CTAStrip
        eyebrow={t.home.final_cta_eyebrow}
        headline={t.home.final_cta_main}
        italicTail={t.home.final_cta_italic}
        subtitle={t.home.final_cta_sub}
        buttonLabel={t.cta.explore}
        buttonTo="/experiences"
        image="/static/experiences/aracena-riotinto/hero.png"
      />
    </div>
  );
}

/**
 * Filtered grid with FLIP-like enter animation:
 * cards keyed by slug remount on filter change and play `rise-in`.
 */
function FilteredGrid({ items }) {
  // Bump a key whenever the slug-set changes, so the whole grid restarts its stagger
  const setKey = useMemo(() => items.map((e) => e.slug).join("|"), [items]);
  const ref = useRef(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Force a one-shot animation restart by toggling a class
    node.classList.remove("is-mounting");
    void node.offsetWidth; // reflow
    node.classList.add("is-mounting");
  }, [setKey]);
  return (
    <div
      ref={ref}
      className="is-mounting grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14"
      data-testid="filtered-grid"
    >
      {items.map((exp, idx) => (
        <div
          key={exp.slug}
          className="filtered-card opacity-0"
          style={{ animationDelay: `${(idx % 12) * 70}ms` }}
        >
          <ExperienceCard experience={exp} />
        </div>
      ))}
    </div>
  );
}
